import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t, LANGS, isRTL } from '../../utils/i18n';
import { loginLocal, loginGoogle, registerStudent, restoreAccount } from '../../utils/api';
import { wakeApi } from '../../utils/wakeApi';
import TrackPicker from '../common/TrackPicker';
import LogoMark from '../common/LogoMark';
import ForgotPasswordModal from './ForgotPasswordModal';
import type { Student, Lang } from '../../types';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) || '').replace(/\/$/, '');

type Step = 'auth' | 'track_select';

export default function LoginScreen() {
  const { login, lang, setLang } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [step, setStep] = useState<Step>('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [track, setTrack] = useState<'3u' | '4u' | '5u' | 'ms7' | 'ms8' | 'ms9' | 'el1' | 'el2' | 'el3' | 'el4' | 'el5' | 'el6'>('5u');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [wakeProgress, setWakeProgress] = useState<string | null>(null);

  // Pending Google login data — needs track selection before completing
  const [pendingGoogle, setPendingGoogle] = useState<{ student: Record<string, unknown>; token: string } | null>(null);

  // Password recovery: open via "שכחתי סיסמה" link below the login form, or
  // auto-open in reset mode if the URL carries ?reset_token=… (the link
  // delivered to the user's inbox).
  const [showForgot, setShowForgot] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const tk = p.get('reset_token') || p.get('token');
      // Only auto-open if the URL path looks like a reset landing, to avoid
      // catching unrelated `token` query params from other flows.
      if (tk && window.location.pathname.includes('reset-password')) {
        setResetToken(tk);
        setShowForgot(true);
      }
    } catch { /* ignore */ }
  }, []);

  const rtl = isRTL(lang);

  // Wake API before any auth call. Render free-tier dynos sleep after ~15min
  // and return 503 with no CORS headers, which would otherwise look like a
  // login failure to the user.
  const ensureApiWarm = async (): Promise<boolean> => {
    if (!API_BASE) return true; // no API configured (dev)
    const warm = await wakeApi(API_BASE, (msg) => setWakeProgress(msg));
    setWakeProgress(null);
    if (!warm) {
      setError(t('login_server_waking', lang));
      return false;
    }
    return true;
  };

  // Check URL hash for OAuth callback (implicit flow returns #access_token=...)
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token')) return;

    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    if (!accessToken) return;

    // Clear the hash from URL
    window.history.replaceState(null, '', window.location.pathname);

    // Use access token to get user info from Google, then pass to backend.
    // Wake the API first so the loginGoogle() call doesn't die on cold-start
    // CORS — Google userinfo is unaffected (it's hitting Google directly).
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!r.ok) throw new Error(`Google userinfo request failed: ${r.status}`);
        const userinfo: { email?: string; name?: string; given_name?: string } = await r.json();
        const ui = {
          email: userinfo.email || '',
          name: userinfo.name || userinfo.given_name || 'Google User',
        };

        if (!(await ensureApiWarm())) return;

        const res = await loginGoogle(accessToken, ui);
        const student = res.student as Record<string, unknown>;
        if (student.track) {
          login(student as unknown as Student, res.token);
        } else {
          setPendingGoogle({ student, token: res.token });
          setStep('track_select');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('login_google_failed', lang));
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, lang]);

  // Google redirect-based OAuth (bypasses COOP popup issues).
  // The redirect_uri MUST exactly match a value whitelisted in Google Cloud
  // Console > Credentials > OAuth client. Vercel auto-generates a unique
  // *.vercel.app URL for every deploy, which would never be whitelisted, so
  // we pin to the canonical production origin unless we're on localhost.
  const handleGoogleRedirect = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError(t('login_google_not_configured', lang));
      return;
    }
    const envOverride = (import.meta.env.VITE_OAUTH_REDIRECT_URI as string | undefined)?.replace(/\/$/, '');
    const isLocal = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname);
    const redirectUri = envOverride || (isLocal ? window.location.origin : 'https://bagrut.1bigfam.com');
    const scope = 'openid email profile';
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&prompt=select_account`;
    window.location.href = url;
  };

  // Local auth submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!(await ensureApiWarm())) return;
      if (mode === 'register') {
        const res = await registerStudent({ name, email, password, track });
        // Always route new registrants through track_select so middle-school
        // students can pick a grade even if the inline picker defaulted to
        // a Bagrut tier.
        setPendingGoogle({ student: res.student as Record<string, unknown>, token: res.token });
        setStep('track_select');
      } else {
        const res = await loginLocal(email, password);
        const student = res.student as unknown as Student;
        if (!student.track) {
          setPendingGoogle({ student: res.student, token: res.token });
          setStep('track_select');
        } else {
          login(student, res.token);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Complete login after track selection. Guest mode was removed — the only
  // path into track_select is now a NEW Google user who still needs to pick
  // a track. If pendingGoogle is somehow missing, bail back to the auth form
  // rather than minting a throwaway local account.
  const handleTrackConfirm = () => {
    if (!pendingGoogle) {
      setStep('auth');
      return;
    }
    const student: Student = {
      ...(pendingGoogle.student as unknown as Student),
      track,
    };
    login(student, pendingGoogle.token);
  };

  // ── Track Selection Screen ──────────────────────────────────
  if (step === 'track_select') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" dir={rtl ? 'rtl' : 'ltr'}>
        <div className="glass-card p-8 w-full max-w-md screen-transition">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3"><LogoMark size={64} /></div>
            <h2 className="text-2xl font-extrabold text-slate-100">
              {t('track_choose', lang)}
            </h2>
            <p className="text-sm text-slate-400 mt-2">
              {t('track_change_later', lang)}
            </p>
          </div>

          <div className="mb-6">
            <TrackPicker value={track} onChange={setTrack} />
          </div>

          <button
            onClick={handleTrackConfirm}
            className="btn-primary w-full text-lg py-3"
          >
            {t('track_start', lang)} 🚀
          </button>

          <button
            onClick={() => { setStep('auth'); setPendingGoogle(null); }}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-300 mt-4 transition"
          >
            ← {t('btn_back', lang)}
          </button>
        </div>
      </div>
    );
  }

  // ── Auth Screen ─────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4" dir={rtl ? 'rtl' : 'ltr'}>
      <div className="glass-card p-8 w-full max-w-md screen-transition">
        {/* Language Switcher */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {(Object.entries(LANGS) as [Lang, { label: string; flag: string }][]).map(([code, info]) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              className={`text-sm px-3 py-1.5 rounded-full border transition ${
                code === lang
                  ? 'border-accent text-accent-light bg-accent/10 font-bold shadow-lg shadow-purple-500/10'
                  : 'border-slate-600/40 text-slate-400 hover:text-slate-200 hover:border-slate-500'
              }`}
            >
              {info.flag} {info.label}
            </button>
          ))}
        </div>

        {/* Logo */}
        <div className="text-center mb-10">
          <div
            className="inline-block relative mb-4"
            style={{ filter: 'drop-shadow(0 8px 24px rgba(124,58,237,.35))' }}
          >
            <LogoMark size={88} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-l from-purple-400 via-cyan-400 to-pink-400 bg-clip-text text-transparent">
            {t('app_title', lang)}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            {t('login_subtitle', lang)}
          </p>
        </div>

        {/* Google Sign-In — only shown when VITE_GOOGLE_CLIENT_ID is configured */}
        {GOOGLE_CLIENT_ID && (
          <div className="mb-4">
            <button
              onClick={handleGoogleRedirect}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              {t('login_google', lang)}
            </button>
          </div>
        )}

        {wakeProgress && (
          <div
            className="glass-card p-3 mb-4 flex items-center gap-3 text-sm text-slate-300"
            style={{ borderColor: 'rgba(34,211,238,.30)' }}
          >
            <span className="inline-block w-3 h-3 rounded-full animate-pulse" style={{ background: 'var(--color-accent)' }} />
            <span className="flex-1">{wakeProgress}</span>
          </div>
        )}

        {loading && !wakeProgress && (
          <div className="text-center text-sm text-slate-400 mb-4">
            {t('login_connecting', lang)}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-slate-700" />
          <span className="text-xs text-slate-500">
            {t('login_or_email', lang)}
          </span>
          <div className="flex-1 h-px bg-slate-700" />
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <input
                type="text"
                placeholder={t('login_full_name', lang)}
                aria-label={t('login_full_name', lang)}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <div className="flex gap-2 justify-center">
                {(['3u', '4u', '5u'] as const).map((tr) => (
                  <button
                    key={tr}
                    type="button"
                    onClick={() => setTrack(tr)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold transition ${
                      track === tr
                        ? 'bg-accent text-white'
                        : 'bg-navy-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {tr.replace('u', ` ${t('track_units', lang)}`)}
                  </button>
                ))}
              </div>
            </>
          )}
          <input
            type="email"
            placeholder={t('login_email', lang)}
            aria-label={t('login_email', lang)}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder={t('login_password', lang)}
            aria-label={t('login_password', lang)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && (
            <div className="text-red-400 text-sm text-center bg-red-500/10 rounded-xl py-2 px-3">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? t('loading', lang) : t(mode === 'register' ? 'login_register' : 'login_submit', lang)}
          </button>

          {mode === 'login' && (
            <div className="flex flex-col items-center gap-1 mt-2">
              <button
                type="button"
                onClick={() => { setResetToken(null); setShowForgot(true); }}
                className="text-xs text-slate-400 hover:text-violet-300"
              >
                שכחתי סיסמה
              </button>
              {/* Restore option appears only after a failed login — the user
                  who got 401 might have a soft-deleted account waiting to be
                  recovered. */}
              {error && email && password && (
                <button
                  type="button"
                  onClick={async () => {
                    setError('');
                    setLoading(true);
                    try {
                      const res = await restoreAccount(email, password);
                      const student = (res as unknown as { user: Student }).user;
                      login(student, res.token);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Restore failed');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="text-xs text-amber-300 hover:text-amber-200"
                >
                  שחזרי חשבון שמחקתי (תוך 30 ימים)
                </button>
              )}
            </div>
          )}
        </form>

        <ForgotPasswordModal
          open={showForgot}
          initialToken={resetToken}
          onClose={() => setShowForgot(false)}
        />

        {/* Toggle Login/Register */}
        <div className="text-center mt-6">
          <button
            onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
            className="text-accent-light text-sm hover:underline"
          >
            {t(mode === 'login' ? 'login_no_account' : 'login_has_account', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
